(ns minorodata.connections.events
  (:require
   [applied-science.js-interop :as js-interop]
   [clojure.set]
   [minorodata.app.handlers :refer [dispatch reg-event-fx]]
   [minorodata.app.posthog-events :as posthog-events]
   [minorodata.app.utils :as app.utils :refer [dispatch-async]]
   [minorodata.connections.model :refer [->extract ->extract-job
                                         ->extract-panel ->extract-task-status
                                         ->filestore-data ->filestore-status
                                         ->source ->source-connection
                                         ->sources-panel ->user-connectors
                                         ->user-synchronizers ->warehouse-data
                                         ->warehouse-meta ->warehouse-status
                                         ->deleted-source ->deleted-extract]]
   [minorodata.utils :as utils]
   [mold-http.core :refer [reg-event-http]]
   [re-frame.core :as re-frame]
   [re-posh.core :as re-posh]))



;; Sink  -> Warehouse
;; Store -> Filestore


;; =============================================================================
;; Fetch warehouse status
;; =============================================================================

(defn report-error [msg error]
  (js/console.error msg error)
  [:app.events/add-snack :error (str "Failed to " msg)])


(defn- source-type-for [source-id]
  @(re-posh/subscribe [:source/prop [:source/source-id source-id] :source/source-type]))


(defn warehouse-status-request [_ {:keys [user-id role-id refresh-data?]
                                   :or {refresh-data? true}}]
  (let [user    @(re-posh/subscribe [:user/pull])
        user-id (if (or role-id user-id)
                  user-id
                  (:user/uid user))
        role-id (if user-id
                  (:user/role-id user)
                  role-id)]
    {:http-xhrio
     {:id  :warehouse-status-request
      :url "/api/sink-status"
      :params (app.utils/assoc-some {}
                                    :role-id role-id
                                    :user-id user-id)
      :cb-params {:refresh-data? refresh-data?}}}))

(defn warehouse-status-callback [_ [error results {:keys [refresh-data?]
                                                   :or   {refresh-data? true}}]]
  (if (some? error)
    {:dispatch-n [(report-error "fetch Warehouse status" error)
                  [:connections.api/fetch-warehouse-data]]}
    (if (nil? (:sink results))
      {:dispatch [:warehouse-status/delete]}
      {:dispatch-n             (cond-> [[:warehouse-status/add (->warehouse-status results)]]
                                 refresh-data? (concat [[:connections.api/fetch-warehouse-data]]))
       :unleash/update-context {:warehouse-type (:sink results)}})))

(reg-event-http
 :connections.api/fetch-warehouse-status
 warehouse-status-request
 warehouse-status-callback)


;; =============================================================================
;; Fetch warehouse data
;; =============================================================================

(defn warehouse-data-request [_ _]
  {:http-xhrio
   {:id  :warehouse-data-request
    :url "/api/sink"}})

(defn warehouse-data-callback [_ [error results]]
  (if (some? error)
    {:dispatch (report-error "fetch Warehouse data" error)}
    {:dispatch [:warehouse-data/add (->warehouse-data results)]}))

(reg-event-http
 :connections.api/fetch-warehouse-data
 warehouse-data-request
 warehouse-data-callback)


;; =============================================================================
;; A flow to save warehouse and check status of both warehouse and filestore
;; =============================================================================

(reg-event-fx
 :connections.api/save-warehouse-flow
 (fn [_ [{:keys [params]}]]
   {:async-flow
    {:first-dispatch [:connections.api/save-warehouse params]}}))



;; =============================================================================
;; Save warehouse
;; =============================================================================

(defn warehouse-save-request [_ [params]]
  {:http-xhrio
   {:id     :warehouse-save-request
    :method :post
    :params (app.utils/clean-up-query-recursive params)
    :url    "/api/sink"}})

(defn warehouse-save-callback [_ [error _]]
  (if (some? error)
    {:dispatch (report-error "save Warehouse" error)}
    {:dispatch-n [[:connections.api/fetch-filestore-status]
                  [:connections.api/fetch-warehouse-status]]}))

(reg-event-http
 :connections.api/save-warehouse
 warehouse-save-request
 warehouse-save-callback)


;; =============================================================================
;; Delete warehouse
;; =============================================================================

(defn warehouse-delete-request [_ [params]]
  {:http-xhrio
   {:id     :warehouse-delete-request
    :method :post
    :params params
    :url    "/api/sink"}})

(defn warehouse-delete-callback [_ [error _]]
  (if (some? error)
    {:dispatch (report-error "delete Warehouse" error)}
    {:dispatch-n [[:warehouse-data/delete]
                  [:connections.api/fetch-warehouse-status]]}))

(reg-event-http
 :connections.api/delete-warehouse
 warehouse-delete-request
 warehouse-delete-callback)


;; =============================================================================
;; Fetch warehouse metadata
;; =============================================================================

(defn warehouse-metadata-request [_ [{:keys [object catalog]}]]
  {:http-xhrio
   {:id        :warehouse-metadata-request
    :method    :post
    :url       "/api/sink-metadata"
    :params    (app.utils/assoc-some
                {:object object}
                :catalog catalog)
    :cb-params {:object object}}})

(defn warehouse-metadata-callback [_ [error result {:keys [object]}]]
  (if (some? error)
    {:dispatch (report-error "fetch Warehouse metadata" error)}
    {:dispatch [:warehouse-meta/update (->warehouse-meta {object result})]}))

(reg-event-http
 :connections.api/fetch-warehouse-meta
 warehouse-metadata-request
 warehouse-metadata-callback)


;; =============================================================================
;; Fetch filestore status
;; =============================================================================

(defn filestore-status-request [_ _]
  {:http-xhrio
   {:id  :filestore-status-request
    :url "/api/store-status"}})

(defn filestore-status-callback [_ [error results]]
  (if (some? error)
    {:dispatch (report-error "fetch Filestore status" error)}
    (if (nil? (:store results))
      {:dispatch [:filestore-status/delete]}
      {:dispatch-n [[:filestore-status/delete]
                    [:filestore-status/add (->filestore-status results) {:replace true}]
                    [:connections.api/fetch-filestore-data]]})))

(reg-event-http
 :connections.api/fetch-filestore-status
 filestore-status-request
 filestore-status-callback)


;; =============================================================================
;; Fetch filestore data
;; =============================================================================

(defn filestore-data-request [_ _]
  {:http-xhrio
   {:id  :filestore-data-request
    :url "/api/store"}})

(defn filestore-data-callback [_ [error results]]
  (if (some? error)
    {:dispatch (report-error "fetch Filestore data" error)}
    {:dispatch [:filestore-data/add (->filestore-data results)]}))

(reg-event-http
 :connections.api/fetch-filestore-data
 filestore-data-request
 filestore-data-callback)


;; =============================================================================
;; Save filestore
;; =============================================================================

(defn filestore-save-request [_ [params]]
  {:http-xhrio
   {:id     :filestore-save-request
    :method :post
    :params params
    :url    "/api/store"}})

(defn filestore-save-callback [_ [error _]]
  (if (some? error)
    {:dispatch (report-error "save Filestore" error)}
    {:dispatch [:connections.api/fetch-filestore-status]}))

(reg-event-http
 :connections.api/save-filestore
 filestore-save-request
 filestore-save-callback)


;; =============================================================================
;; Delete filestore
;; =============================================================================

(defn filestore-delete-request [_ [params]]
  {:http-xhrio
   {:id     :filestore-delete-request
    :method :post
    :params params
    :url    "/api/store"}})

(defn filestore-delete-callback [_ [error _]]
  (if (some? error)
    {:dispatch (report-error "delete Filestore" error)}
    {:dispatch-n [[:filestore-data/delete]
                  [:connections.api/fetch-filestore-status]]}))

(reg-event-http
 :connections.api/delete-filestore
 filestore-delete-request
 filestore-delete-callback)


;; =============================================================================
;; Fetch sources list
;; =============================================================================

(defn sources-request [_ [{:keys [pagination sort]}]]
  (let [{panel-sort :sources-panel/sort
         panel-pagination :sources-panel/pagination}
        @(re-posh/subscribe [:sources-panel/pull])
        sort (or sort panel-sort)
        pagination (merge {:items-per-page 10000 :page 1}
                          panel-pagination
                          pagination)]
    {:http-xhrio
     {:id  :sources-request
      :params (app.utils/assoc-some {:pagination pagination}
                                    :sort       sort)
      :url "/api/sources-v2"
      :cb-params {:pagination pagination
                  :sort       sort}}}))

(defn sources-callback [_ [error results {:keys [sort]}]]
  (if (some? error)
    {:dispatch (report-error "fetch sources" error)}
    (let [raw-sources (:results results)
          sources     (map-indexed #(->source (assoc %2 :order %1)) raw-sources)
          old-sources @(re-posh/subscribe [:source/pull-many])

          removed-sources (filter (fn source-removed? [source]
                                    (nil? (utils/find-by-field sources :source/source-id (:source/source-id source))))
                                  old-sources)]
      {:dispatch-n (conj (mapv #(vector :source/delete (:db/id %))
                               removed-sources)
                         [:source/add-many sources]
                         [:sources-panel/update
                          (->sources-panel {:sources   sources
                                            :order-by  (:order-by sort)
                                            :order-dir (:order-dir sort)})
                          {:replace true}])})))

(reg-event-http
 :connections.api/fetch-sources
 sources-request
 sources-callback)


(re-frame/reg-event-fx
 :connections.events/load-data
 (fn [_ _]
   {:dispatch-n [[:connections.api/fetch-warehouse-status]
                 [:connections.api/fetch-filestore-status]
                 [:connections.api/fetch-sources]]}))


;; =============================================================================
;; Create source
;; =============================================================================

(defn create-source-callback [_ [error res {:keys [on-success source-type]}]]
  (if (nil? error)
    (do
      (when on-success (on-success res))
      {:dispatch-n [[:connections.api/fetch-sources]
                    [:app.events/add-snack :success "Successfully created a source"]]
       :posthog/capture {:event (:source/created posthog-events/events)
                         :props {:source-type source-type}}})
    {:dispatch (report-error "create a source " error)}))

(defn create-source-request [_ [params {:keys [on-success]}]]
  {:http-xhrio
   {:id     :create-source-request
    :method :post
    :url    "/api/sources-v2"
    :params params
    :cb-params {:on-success  on-success
                :source-type (:source-type params)}}})

(reg-event-http
 :connections.api/create-source
 create-source-request
 create-source-callback)


;; =============================================================================
;; Delete source
;; =============================================================================

(defn delete-source-callback [_ [error _ {:keys [on-success]}]]
  (if (nil? error)
    (do
      (when on-success (on-success))
      {:dispatch-n [[:connections.api/fetch-sources]
                    [:app.events/add-snack :success "Successfully archived a source"]]})
    {:dispatch (report-error "archive a source" error)}))

(defn delete-source-request [_ [source-id params]]
  {:http-xhrio
   {:id     :delete-source-request
    :method :delete
    :url    (str "/api/sources-v2/" source-id)
    :cb-params params}})

(reg-event-http
 :connections.api/delete-source
 delete-source-request
 delete-source-callback)


;; =============================================================================
;; Edit source
;; =============================================================================

(defn edit-source-callback [_ [error _ {:keys [on-success]}]]
  (if (nil? error)
    (do
      (when on-success (on-success))
      {:dispatch-n [[:connections.api/fetch-sources]
                    [:app.events/add-snack :success "Successfully updated a source"]]})
    {:dispatch (report-error "update a source" error)}))

(defn edit-source-request [_ [source-id params cb-params]]
  {:http-xhrio
   {:id     :edit-source-request
    :method :put
    :url    (str "/api/sources-v2/" source-id)
    :params (select-keys params [:source :name :description])
    :cb-params cb-params}})

(reg-event-http
 :connections.api/edit-source
 edit-source-request
 edit-source-callback)


;; =============================================================================
;; Get extracts for source
;; =============================================================================

(def default-query
  {:pagination {:items-per-page 50
                :page           1}
   :sort       {:order-by  "statuses.status, statuses.created_at"
                :order-dir "DESC"}})

(defn fetch-extracts-request [_ [{:keys [source-id] :as params}]]
  (let [{:extract-panel/keys [sort pagination filter]}
        @(re-posh/subscribe [:extract-panel/pull])]
    {:http-xhrio
     {:id        (keyword source-id)
      :method    :get
      :params    (-> default-query
                     (app.utils/assoc-some :pagination (merge pagination (:pagination params)))
                     (app.utils/assoc-some :sort       (merge sort (:sort params)))
                     (app.utils/assoc-some :from       (or (:from filter) (:from params)))
                     (app.utils/assoc-some :to         (or (:to filter) (:to params))))
      :url       (str "/api/sources-v2/" source-id "/extracts")
      :cb-params params}}))

(defn stale-master-id-deletes
  [source-id fresh-extracts]
  (let [existing @(re-posh/subscribe [:extract/pull-many])]
    (->> fresh-extracts
         (mapcat (fn [{:keys [extract-id master-id]}]
                   (->> existing
                        (filter #(and (some? master-id)
                                      (= (:extract/master-id %) master-id)
                                      (= (:extract/source-id %) source-id)
                                      (not= (:extract/extract-id %) extract-id)))
                        (map :extract/extract-id))))
         distinct
         (mapv #(vector :extract/delete [:extract/extract-id %])))))

(defn fetch-extracts-callback
  [_ [error res {:keys [source-id
                        replace
                        reconcile-stale-ids?
                        master-id
                        active-extract-id
                        extract-details-drawer?]}]]
  (if (some? error)
    {:dispatch (report-error "fetch extracts" error)}

    (let [{:extract-panel/keys [latest-order-index]}
          @(re-posh/subscribe [:extract-panel/pull])

          {:app-route/keys [params query]}
          @(re-posh/subscribe [:app-route/pull])

          {:keys [results pagination sort]} res
          results-count   (count results)
          [start-idx end-idx] (if replace
                                [0 results-count]
                                [latest-order-index
                                 (+ latest-order-index results-count)])
          extracts        (mapv (fn [idx extract]
                                  (assoc extract :order idx))
                                (range start-idx end-idx)
                                results)
          panel           (-> {:items              extracts
                               :source-id          source-id
                               :latest-order-index end-idx}
                              (assoc :pagination pagination
                                     :sort sort)
                              ->extract-panel)

          ;; TODO this manual transform update is a quick fix for now, should be handled properly by mold library
          update-previous
          (mapv (fn [{:keys [extract-id] :as extract}]
                  [:extract/update
                   [:extract/extract-id extract-id]
                   (->extract extract)
                   {:replace true}])
                extracts)

          stale-deletes
          (when reconcile-stale-ids?
            (stale-master-id-deletes source-id extracts))

          replacement-extract-id
          (when master-id
            (some (fn [{fresh-master-id :master-id
                        fresh-extract-id :extract-id}]
                    (when (= fresh-master-id master-id)
                      fresh-extract-id))
                  results))

          id-changed?
          (and active-extract-id
               replacement-extract-id
               (not= active-extract-id replacement-extract-id))

          update-route?
          (and extract-details-drawer?
               id-changed?
               (= active-extract-id (:extract-id params)))

          dispatches
          (conj (into update-previous stale-deletes)
                [:extract-panel/update panel {:replace replace}])]

      {:dispatch-n
       (cond-> dispatches
         update-route?
         (conj [:app.events/navigate-replace
                :extract-details
                {:source-id  source-id
                 :extract-id replacement-extract-id}
                query]))})))

(reg-event-http
 :connections.api/fetch-extracts
 fetch-extracts-request
 fetch-extracts-callback)


;; =============================================================================
;; Get single extract for source
;; =============================================================================

(defn fetch-extract-callback [_ [error result {:keys [on-success]}]]
  (if (some? error)
    {:dispatch (report-error "fetch extract" error)}
    (let [extract (->extract result)]
      (when on-success (js/setTimeout on-success 100))
      {:dispatch [:extract/add extract]})))

(defn fetch-extract-request [_ [{:keys [source-id extract-id] :as params}]]
  {:http-xhrio
   {:id        (keyword source-id)
    :method    :get
    :url       (str "/api/sources-v2/" source-id "/extracts/" extract-id)
    :cb-params params}})

(reg-event-http
 :connections.api/fetch-extract
 fetch-extract-request
 fetch-extract-callback)


;; =============================================================================
;; Test Source Credentials
;; =============================================================================

(defn test-connection-callback [_ [{:keys [last-error-code failure] :as error} {:keys [body]}]]
  (cond
    (or (= last-error-code 8) (= failure :timeout))
    {:dispatch [:source-connection/add {:source-connection/connection-status :failed
                                        :source-connection/connection-message "Authentication request timed out"}]}

    (some? error)
    {:dispatch-n [[:app.events/add-snack :error "Source Connection Check Failed"]
                  [:source-connection/add {:source-connection/connection-status :failed
                                           :source-connection/connection-message "Connection error"}]]}

    :else
    {:dispatch [:source-connection/add (->source-connection body)]}))

(defn test-connection-request [_ [{:keys [source params]}]]
  {:http-xhrio
   {:method  :post
    :timeout 5000
    :url     "/api/sources-v2/test-source"
    :params  {:source (str source)
              :params (str params)}}})

(reg-event-http
 :connections.api/test-connection
 test-connection-request
 test-connection-callback)

;; =============================================================================
;; Create extract
;; =============================================================================

(defn create-extract-callback [_ [error results source-id]]
  (if (nil? error)
    (let [extract-id  (get-in results [:data :extract-id])
          source-type @(re-posh/subscribe [:source/prop [:source/source-id source-id] :source/source-type])
          connector   @(re-frame/subscribe [:connections.subs/connector-for-source-id source-id])

          open-form-when-created (:open-form-when-created connector)]
      (cond-> {:dispatch-n [[:app.events/add-snack :success "Successfully created an extract"]
                            [:connections.api/fetch-extracts {:source-id source-id
                                                              :pagination {:page 1}}]]}

        open-form-when-created
        (assoc :async-flow {:rules [{:when     :seen?
                                     :events   :connections.api/fetch-extracts-success
                                     :dispatch [:connector/add {:connector/action :edit-extract
                                                                :connector/type   source-type
                                                                :connector/id     extract-id}]
                                     :halt?    true}]})))
    {:dispatch (report-error "create an extract" error)}))

(defn create-extract-request [_ [source-id params]]
  {:http-xhrio
   {:id        :create-extract-request
    :method    :post
    :cb-params source-id
    :url       (str "/api/sources-v2/" source-id "/extracts")
    :params    params}})

(reg-event-http
 :connections.api/create-extract
 create-extract-request
 create-extract-callback)


;; =============================================================================
;; Delete extract
;; =============================================================================

(defn delete-extract-callback [_ [error _ {:keys [source-id extract-id]}]]
  (if (nil? error)
    {:dispatch-n [[:connections.api/fetch-extracts {:source-id source-id}]
                  [:extract/delete [:extract/extract-id extract-id]]
                  [:app.events/add-snack :success "Successfully deleted the extract"]]}
    {:dispatch (report-error "delete the extract" error)}))

(defn delete-extract-request [_ [{:keys [source-id extract-id] :as params}]]
  {:http-xhrio
   {:id        :delete-extract-request
    :method    :delete
    :cb-params params
    :url       (str "/api/sources-v2/" source-id "/extracts/" extract-id)}})

(reg-event-http
 :connections.api/delete-extract
 delete-extract-request
 delete-extract-callback)


;; =============================================================================
;; Edit extract
;; =============================================================================

(defn- schedule-updated-capture [source-id extract-count schedule-spec]
  (let [cron (:cron schedule-spec)]
    {:event (:extract/schedule-updated posthog-events/events)
     :props {:source-type   (source-type-for source-id)
             :action        (if (and (seq cron) (not= cron app.utils/empty-cron)) "scheduled" "unscheduled")
             :extract-count extract-count}}))

(defn edit-extract-callback [_ [error result {:keys [source-id extract-id silent? on-success
                                                     extract-details-drawer? schedule-change? data]}]]
  (if (nil? error)
    ;; The backend soft-deletes and recreates the extract on every update, so the
    ;; response carries a new extract-id (same master-id) — read it here so the
    ;; old, now-inactive id can be dropped and the drawer/route can follow it.
    (let [new-extract    (->extract result)
          new-extract-id (:extract/extract-id new-extract)
          id-changed?    (and (some? new-extract-id) (not= extract-id new-extract-id))
          {:app-route/keys [params query]} @(re-posh/subscribe [:app-route/pull])]
      (when on-success (on-success))
      {:dispatch-n [(when (some? new-extract-id)
                      [:extract/add new-extract])
                    [:connections.api/fetch-extracts {:source-id  source-id
                                                      :pagination {:page 1 :items-per-page 50}
                                                      :replace    true}]
                    (when id-changed?
                      [:extract/delete [:extract/extract-id extract-id]])
                    (when (and extract-details-drawer? id-changed? (= extract-id (:extract-id params)))
                      [:app.events/navigate-replace :extract-details
                       {:source-id source-id :extract-id new-extract-id}
                       query])
                    (when-not silent?
                      [:app.events/add-snack :success "Successfully updated an extract"])]
       :posthog/capture (if schedule-change?
                          (schedule-updated-capture source-id 1 (:schedule-spec data))
                          {:event (:extract/edited posthog-events/events)
                           :props {:source-type (source-type-for source-id)}})})
    {:dispatch (report-error "update an extract" error)}))

(defn edit-extract-request [_ [{:keys [source-id extract-id data] :as props}]]
  {:http-xhrio
   {:id        :edit-extract-request
    :method    :put
    :cb-params props
    :url       (str "/api/sources-v2/" source-id "/extracts/" extract-id)
    :params    data}})

(reg-event-http
 :connections.api/edit-extract
 edit-extract-request
 edit-extract-callback)

;; =============================================================================
;; Bulk Schedule extracts of a source
;; =============================================================================

(defn bulk-schedule-extracts-callback
  [_ [error _ {:keys [source-id
                      master-id
                      active-extract-id
                      extract-details-drawer?
                      on-success
                      on-failure
                      data]}]]
  (if (nil? error)
    (do
      (when on-success (on-success))
      {:dispatch
       [:connections.api/fetch-extracts
        {:source-id               source-id
         :pagination              {:page 1 :items-per-page 50}
         :replace                 true
         :reconcile-stale-ids?    true
         :master-id               master-id
         :active-extract-id       active-extract-id
         :extract-details-drawer? extract-details-drawer?}]
       :fx
       [(when (or (:schedule-spec data) (:unschedule? data))
          [:posthog/capture (schedule-updated-capture source-id
                                                      (count (:extract-ids data))
                                                      (:schedule-spec data))])]})
    (do
      (when on-failure (on-failure))
      {:dispatch (report-error "bulk schedule extracts" error)})))

(defn bulk-schedule-extracts-request [_ [{:keys [source-id data] :as props}]]
  {:http-xhrio
   {:id        :bulk-schedule-extracts-request
    :method    :post
    :cb-params props
    :url       (str "/api/sources-v2/" source-id "/schedule")
    :params    data}})

(reg-event-http
 :connections.api/bulk-schedule-extracts
 bulk-schedule-extracts-request
 bulk-schedule-extracts-callback)

;; =============================================================================
;; Fetch Extract statuses for message-queue enables sources
;; =============================================================================


(defn fetch-extract-statuses-for-source-request [_ [source-id params]]
  {:http-xhrio
   {:id     :fetch-extract-statuses-for-source-request
    :url    (str "/api/sources-v2/" source-id "/extracts-statuses")
    :method :get
    :cb-params params}})

(defn fetch-extract-statuses-for-source-callback [_ [error resp {:keys [on-success]}]]
  (if (some? error)
    (let [message "Failed to fetch extracts status"]
      {:dispatch-n [(report-error "fetch extracts status" error)
                    [:app.api/log :error message error]]})

    (let [statuses      (:statuses resp)
          status-models (map ->extract-task-status statuses)]
      (when on-success (on-success status-models))
      {:dispatch [:extract-task-status/add-many status-models]})))

(reg-event-http
 :connections.api/fetch-extract-statuses-for-source
 fetch-extract-statuses-for-source-request
 fetch-extract-statuses-for-source-callback)


;; =============================================================================
;; Toaster Poll
;; =============================================================================

(def TOASTER_POLLING_INTERVAL_MS 2500)


(defn toaster-poll-request [_ [job-id]]
  {:http-xhrio
   {:url       (str "/api/toaster/job-execution/" job-id)
    :cb-params {:job-id job-id}}})

(defn toaster-poll-callback [_ [error resp {:keys [job-id]}]]
  (if (some? error)
    {:dispatch-n [[:extract-job/delete [:extract-job/id job-id]]
                  (report-error "fetch job status" error)
                  [:app.api/log :error "Failed to fetch job status" error]]}

    (let [results   (:results resp)
          job-id    (:job-execution/id results)
          status    (:job/status results)
          finished? (or (= "failed" status)
                        (= "success" status))]
      (app.utils/assoc-some
       {:dispatch [:connections.events/set-job-statuses results]}
       :dispatch-later (when-not finished?
                         [{:ms       TOASTER_POLLING_INTERVAL_MS
                           :dispatch [:connections.api/poll-by-id job-id]}])))))


(reg-event-http
 :connections.api/poll-by-id
 toaster-poll-request
 toaster-poll-callback)


;; =============================================================================
;; Toaster Start Extract
;; =============================================================================

(defn extracts-toaster-start-request [_ [{:keys [extract-id] :as options}]]
  (let [extract        @(re-posh/subscribe [:extract/pull [:extract/extract-id extract-id]])
        {:extract/keys [extract-id source-id client-id name]} extract
        source         @(re-posh/subscribe [:source/pull [:source/source-id source-id]])
        {:source/keys [source-type]} source
        container-name (str source-type "-container")]
    {:http-xhrio
     {:id        :extract-tiberian-request
      :url       "/api/toaster/job/one-time"
      :method    :post
      :params    {"client/id" client-id
                  "job/tasks" [{"task/type"   "extract"
                                "task/params" {"launch-type"     "FARGATE" ;; FARGATE or ECS
                                               "task-definition" (:source/task-name source)
                                               "cluster"         "minoro"
                                               "extract-id"      extract-id
                                               "task-params"     {"overrides"
                                                                  {"container-overrides"
                                                                   [{"command" ["--extract-id" extract-id "--client-id" client-id]
                                                                     "name"    container-name}]}}}}]}
      :cb-params {:extract-id extract-id
                  :name       name
                  :source     (:source options)
                  :source-id  (:source-id options)}}
     :posthog/capture {:event (:extract/run-triggered posthog-events/events)
                       :props {:extract-id  extract-id
                               :source-type source-type
                               :name        name}}}))

(defn extracts-toaster-start-callback [_ [error resp {:keys [extract-id name source source-id]}]]
  (let [mq-enabled (:message-queue-enabled source)]
    (if (some? error)
      (let [message "Failed to start extract"]
        {:dispatch-n [[:app.events/add-snack :error message {:data-cy "job-status-failed-to-start"}]
                      [:app.api/log :error message error]]})

      (let [results        (:results resp)
            job-builder-id (:job-execution/id results)
            text           (str "Started extract " name)]
        {:dispatch-n     [[:app.events/add-snack :success text {:data-cy "job-status-running"}]
                          ;; Old status updating scheme which does polling
                          (when-not mq-enabled [:connections.events/add-initial-job results extract-id])
                          (when-not mq-enabled [:connections.api/poll-by-id job-builder-id])

                          ;; New on-demand status status updating scheme
                          (when mq-enabled [:connections.api/fetch-extract-statuses-for-source source-id])]

         ;; Since we don't poll anymore, might as well schedule an extra update
         :dispatch-later [(when mq-enabled
                            {:ms       1000
                             :dispatch [:connections.api/fetch-extract-statuses-for-source source-id]})]}))))

(reg-event-http
 :connections.api/start-extract-by-id
 extracts-toaster-start-request
 extracts-toaster-start-callback)


;;
;; Job status
;;

(reg-event-fx
 :connections.events/add-initial-job
 (fn [_ [results extract-id]]
   (let [extract   @(re-posh/subscribe [:extract/pull [:extract/extract-id extract-id]])
         job-name  (:extract/name extract)
         job-model (-> {:id           (:job/id results)
                        :name         job-name
                        :execution-id (:job-execution/id results)
                        :status       :starting
                        :extract      extract-id}
                       ->extract-job)]
     {:dispatch [:extract-job/update [:extract-job/id (:job/id results)] job-model]})))


(defn finished-notification [status]
  (let [failed? (= "failed" status)
        type    (if failed? :error :success)
        text    (if failed?
                  "Job failed"
                  "Job finished")
        data-cy (if failed?
                  "job-status-success"
                  "job-status-failure")]
    [:app.events/add-snack type text {:data-cy data-cy}]))


(reg-event-fx
 :connections.events/set-job-statuses
 (fn [_ [results]]
   (let [job-id     (:job/id results)
         job-status (:job/status results)
         finished?  (or (= job-status "failed")
                        (= job-status "success"))
         job-model  (-> {:id         job-id
                         :status     job-status
                         :errors     (:job/errors results)
                         :start-time (:job/start-time results)
                         :end-time   (:job/end-time results)}
                        ->extract-job)]
     {:dispatch-n (app.utils/conj-when
                   [[:extract-job/update [:extract-job/id job-id] job-model]]
                   finished? (finished-notification job-status))})))


;;
;; oAuth 3rd leg request
;;

;; passing source-type, code and redirect-uri

(defn oauth-third-leg-callback [_ [error resp callback-params]]
  (let [{:keys [set-error set-value response-processor oauth-redirect-params]} callback-params]
    (if (nil? error)
      (do
        (set-value resp (response-processor resp oauth-redirect-params))
        {:dispatch [:app.events/add-snack :success "Successfully fetched credentials"]})
      (do
        (set-error error)
        (report-error "fetch credentials" error)))))

(defn oauth-third-leg-request [_ [config]]
  (let [{:keys [params set-error set-value response-processor oauth-redirect-params]} config]
    {:http-xhrio
     {:id        :oauth-third-leg-request
      :method    :post
      :cb-params {:set-error             set-error
                  :set-value             set-value
                  :response-processor    response-processor
                  :oauth-redirect-params oauth-redirect-params}
      :url       "/api/sources-v2/exchange-auth-code"
      :params    params}}))

(reg-event-http
 :connections.api/oauth-third-leg
 oauth-third-leg-request
 oauth-third-leg-callback)


;; =============================================================================
;; Fetch connectors
;; =============================================================================

(defn user-connectors-request [_ _]
  {:http-xhrio
   {:id  :user-connectors-request
    :url "/api/connectors"}})

(defn user-connectors-callback [_ [error results]]
  (if (some? error)
    {:dispatch (report-error "fetch connectors" error)}
    {:dispatch [:user-connectors/add (->user-connectors {:list results})]}))

(reg-event-http
 :connections.api/fetch-user-connectors
 user-connectors-request
 user-connectors-callback)

;; =============================================================================
;; Fetch synchronizers
;; =============================================================================


(defn user-synchronizers-request [_ _]
  (let [client-id (:user/cid @(re-posh/subscribe [:user/pull]))]
    {:http-xhrio
     {:id  :user-synchronizers-request
      :params    {:client-id client-id}
      :url (str "/waffle-iron/synchronizer-source")}}))


(defn user-synchronizers-callback [_ [error results]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error error]}
    {:dispatch [:user-synchronizers/add (->user-synchronizers {:list results})]}))


(reg-event-http
 :connections.api/fetch-user-synchronizers
 user-synchronizers-request
 user-synchronizers-callback)


;; =============================================================================
;; Fetch source metadata
;; =============================================================================


(reg-event-fx
 :connections.api/select-all-field
 (fn [_ [select-all-params]]
   (let [options       (re-posh/subscribe (:options-sub select-all-params))
         current-value (:current-value select-all-params)
         select-fn     (:select-fn select-all-params)]

     (cond
       ;; When current value is empty. This can only happen in two cases:
       ;; * when it's a new extract and nothig has ever been selected yet
       ;; * when the user changed table/schema and column value had been reset
       ;;   as a dependant one
       (and (js-interop/call js/Array :isArray current-value)
            (= 0 (js-interop/get current-value :length)))
       (select-fn @options)

       ;; If there is already a set current-value, we need to make sure that all
       ;; the options are valid. For instance, there were cases when a column
       ;; was removed from the table and now we need to be able to re-save the
       ;; extract with new columns, updating the value if we detect that
       ;; current-value has columns that are not available anymore.
       (and (seq current-value)
            (some? @options)
            (seq @options)
            (not (clojure.set/superset? (set @options) (set current-value))))
       (select-fn (into [] (clojure.set/intersection (set current-value) (set @options)))))
     nil)))


(defn fetch-source-meta-callback [{:keys [db]} [error response callback-params]]
  (let [results  (:results response)

        {:keys [request-params source-id possible-select-all]} callback-params

        schema (get-in request-params [:input :schema])
        table  (get-in request-params [:input :table])

        object-key (some-> (:object request-params) keyword)
        path  (filter identity [:source-metadata object-key source-id schema table])]

    (if (nil? error)
      {:db             (assoc-in db path (get results object-key))
       :dispatch-later [(when (:select-all possible-select-all)
                          {:ms       5
                           :dispatch [:connections.api/select-all-field possible-select-all]})]}

      {:dispatch (report-error "fetch source metadata" error)})))

(defn fetch-source-meta-request [_ [source-id request-params possible-select-all]]
  {:http-xhrio
   {:id        (keyword (str "fetch-metadata-request-" source-id "-" (:object request-params) "-" request-params))
    :cb-params {:source-id source-id :request-params request-params :possible-select-all possible-select-all}
    :method    :post
    :params    request-params
    :url       (str "/api/sources-v2/" source-id "/partial-metadata")}})

(reg-event-http
 :connections.api/fetch-source-meta
 fetch-source-meta-request
 fetch-source-meta-callback
 :cache? true)


(reg-event-fx
 :connections/maybe-open-editor-console
 (fn [_ _]
   (let [{:keys [code new-tab]} @(re-posh/subscribe [:app-route/prop :app-route/query])]
     (when (and code new-tab)
       {:dispatch [:sql-console-modal.events/toggle-modal true]}))))

(reg-event-fx
 :connections.events/bulk-delete-extracts
 (fn [_ [{:keys [extract-ids source-id on-success]}]]
   (let [delete-extract #(dispatch-async [:connections.api/delete-extract
                                          {:source-id source-id
                                           :extract-id %}])]
     (-> js/Promise
         (.allSettled (map delete-extract extract-ids))
         (.then (fn []
                  (dispatch [:app.events/add-snack :success "Successfully delete extracts"])
                  (when on-success (on-success))))
         (.catch (fn []
                   (dispatch [:app.events/add-snack :error "Failed to delete extracts"])))))
   nil))

(reg-event-fx
 :connections.events/bulk-unschedule-extracts
 (fn [_ [{:keys [extract-ids source-id on-success]}]]
   (-> js/Promise
       (.allSettled [(dispatch-async
                      [:connections.api/bulk-schedule-extracts
                       {:source-id  source-id
                        :data       {:extract-ids extract-ids
                                     :unschedule? true}}])])
       (.then (fn []
                (dispatch [:app.events/add-snack :success "Successfully unscheduled extracts"])
                (when on-success (on-success))))
       (.catch (fn []
                 (dispatch [:app.events/add-snack :error "Failed to unschedule extracts"]))))
   nil))

(defn bulk-schedule-extracts!
  ([source-id data]
   (bulk-schedule-extracts! source-id data nil))

  ([source-id data context]
   (new js/Promise
        (fn [resolve reject]
          (dispatch
           [:connections.api/bulk-schedule-extracts
            (merge {:source-id  source-id
                    :data       data
                    :on-success resolve
                    :on-failure reject}
                   context)])))))

(reg-event-fx
 :connections.events/bulk-pause-extracts
 (fn [_ [{:keys [extract-ids source-id status on-success on-failure]}]]
   (let [data (->> (if (= status "active")
                     {:unpause? true}
                     {:pause? true})
                   (merge {:extract-ids extract-ids}))
         action (if (= status "active")
                  "unpaused"
                  "paused")]
     (-> (bulk-schedule-extracts! source-id data)
         (.then (fn []
                  (dispatch [:app.events/add-snack :success (str "Successfully " action " extracts")])
                  (when on-success (on-success))))
         (.catch (fn []
                   (dispatch [:app.events/add-snack :error (str  "Failed to " action " extracts")])
                   (when on-failure (on-failure))))))
   nil))

(reg-event-fx
 :connections.events/bulk-schedule-extracts
 (fn [_ [{:keys [extract-ids
                 source-id
                 data
                 master-id
                 active-extract-id
                 extract-details-drawer?
                 on-success
                 on-failure]}]]
   (let [data    (merge data {:extract-ids extract-ids})
         context (when extract-details-drawer?
                   {:master-id               master-id
                    :active-extract-id       active-extract-id
                    :extract-details-drawer? true})]
     (-> (bulk-schedule-extracts! source-id data context)
         (.then
          (fn []
            (dispatch
             [:app.events/add-snack
              :success
              "Successfully scheduled extracts"])
            (when on-success
              (on-success))))
         (.catch
          (fn []
            (dispatch
             [:app.events/add-snack
              :error
              "Failed to schedule extracts"])
            (when on-failure
              (on-failure))))))
   nil))

(defn generate-keypair-request [_ [{:keys [passphrase]} {:keys [on-success]}]]
  {:http-xhrio
   {:id        :generate-keypair
    :url       "/api/utils/generate-keypair"
    :method    :post
    :params    {:passphrase passphrase}
    :cb-params {:on-success on-success}}})

(defn generate-keypair-callback [_ [error response {:keys [on-success]}]]
  (let [data (:data response)]

    (if (nil? error)
      (when (fn? on-success) (on-success data))
      {:dispatch [:app.events/add-snack :error "Failed to generate keypair"]})))

(reg-event-http
 :connections.api/generate-keypair
 generate-keypair-request
 generate-keypair-callback)


(defn fetch-extract-statuses-request [_ [{:keys [status]}]]
  {:http-xhrio
   {:id        :fetch-extract-statuses
    :url       "/api/sources-v2/extracts/statuses"
    :method    :get
    :params    (when status {:status status})}})

(defn fetch-extract-statuses-callback [_ [error response]]
  (if (nil? error)
    {:dispatch [:statuses.events/upsert :extract (:statuses response)]}
    {:dispatch [:app.events/add-snack :error "Failed to fetch extract statuses"]}))

(reg-event-http
 :connections.api/fetch-extract-statuses
 fetch-extract-statuses-request
 fetch-extract-statuses-callback)

;; Second id for the same request and the same landing place, so the DAG views can
;; ask for the FULL status list (no `:status` filter) while the caller above asks
;; only for failures — and so each gets its own `:mold-http/loading?` key, which is
;; what the DAG's refresh spinner and status banner read.
(reg-event-http
 :connections.api/fetch-extract-statuses-for-graph
 fetch-extract-statuses-request
 fetch-extract-statuses-callback)




;; =============================================================================
;; Fetch deleted sources list
;; =============================================================================

(defn deleted-sources-request [_ _]
  {:http-xhrio
   {:id  :deleted-sources-request
    :url "/api/sources-v2/inactive"}})

(defn deleted-sources-callback [_ [error results _cb_params]]
  (if (some? error)
    {:dispatch (report-error "fetch archived sources" error)}
    (let [raw-sources (:result results)
          sources     (map-indexed #(->deleted-source (assoc %2 :order %1)) raw-sources)
          old-sources @(re-posh/subscribe [:deleted-source/pull-many])

          removed-sources (filter (fn source-removed? [source]
                                    (nil? (utils/find-by-field sources :deleted-source/source-id (:deleted-source/source-id source))))
                                  old-sources)]
      {:dispatch-n (conj (mapv #(vector :deleted-source/delete (:db/id %))
                               removed-sources)
                         [:deleted-source/add-many sources])})))

(reg-event-http
 :connections.api/fetch-deleted-sources
 deleted-sources-request
 deleted-sources-callback)


;; =============================================================================
;; Fetch deleted extracts list
;; =============================================================================

(defn deleted-extracts-request [_ {:keys [source-id]}]
  {:http-xhrio
   {:id  :deleted-extracts-request
    :url (str "/api/sources-v2/inactive-extracts/" source-id)
    :cb-params {:source-id source-id}}})

(defn deleted-extracts-callback [_ [error results {:keys [source-id]}]]
  (if (some? error)
    {:dispatch (report-error "fetch deleted extracts" error)}
    (let [raw-extracts (:results results)
          extracts     (map-indexed #(->deleted-extract (assoc %2 :order %1)) raw-extracts)
          old-extracts @(re-posh/subscribe [:deleted-extract/pull-many])

          removed-extracts (filter (fn source-removed? [source]
                                     (nil? (utils/find-by-field extracts :deleted-extract/source-id (:deleted-exctract/source-id source))))
                                   old-extracts)]
      {:dispatch-n (conj (mapv #(vector :deleted-extract/delete (:db/id %))
                               removed-extracts)
                         [:deleted-extract/add-many extracts])})))

(reg-event-http
 :connections.api/fetch-deleted-extracts
 deleted-extracts-request
 deleted-extracts-callback)


;; =============================================================================
;; Restore deleted source
;; =============================================================================

(defn restore-deleted-source-request [_ [{:keys [source-id]} {:keys [on-success]}]]
  {:http-xhrio
   {:id        :restore-deleted-source
    :url       "/api/sources-v2/reactivate"
    :method    :post
    :params    {:source-id source-id}
    :cb-params {:on-success on-success}}})

(defn restore-deleted-source-callback [_ [error response {:keys [on-success]}]]
  (let [data (:data response)]

    (if (nil? error)
      (doall
       (when (fn? on-success) (on-success data)))
      {:dispatch [:app.events/add-snack :error "Failed to restore source"]})))

(reg-event-http
 :connections.api/restore-deleted-source
 restore-deleted-source-request
 restore-deleted-source-callback)


;; =============================================================================
;; Restore deleted extract
;; =============================================================================

(defn restore-deleted-extract-request [_ [{:keys [source-id extract-id]} {:keys [on-success]}]]
  {:http-xhrio
   {:id        :restore-deleted-extract
    :url       (str "/api/sources-v2/reactivate/" source-id)
    :method    :post
    :params    {:source-id source-id :extract-id extract-id}
    :cb-params {:on-success on-success}}})

(defn restore-deleted-extract-callback [_ [error response {:keys [on-success]}]]
  (let [data (:data response)]

    (if (nil? error)
      (doall
       (when (fn? on-success) (on-success data)))
      {:dispatch [:app.events/add-snack :error "Failed to restore extract"]})))

(reg-event-http
 :connections.api/restore-deleted-extract
 restore-deleted-source-request
 restore-deleted-source-callback)


